/**
 * Dataset API client.
 */

import { apiClient } from './client';
import type {
  Dataset,
  DatasetRow,
  DatasetColumn,
  DatasetRowReviewStatus,
  DatasetRowsResponse,
  UpdateDatasetDto,
} from '@snxt/snxt-onto-share';

// ─── Datasets ──────────────────────────────────────────────────────────────────

export const datasetApi = {
  list: (): Promise<Dataset[]> =>
    apiClient.get<Dataset[]>('/api/datasets'),

  get: (id: string): Promise<Dataset> =>
    apiClient.get<Dataset>(`/api/datasets/${id}`),

  create: (dto: {
    name: string;
    description?: string;
    knowledge_base_id: string;
    ontology_id?: string;
    ontology_version_id?: string;
    anchor_node_id?: string;
    schema: DatasetColumn[];
  }): Promise<Dataset> =>
    apiClient.post<Dataset>('/api/datasets', dto),

  update: (id: string, dto: UpdateDatasetDto): Promise<Dataset> =>
    apiClient.patch<Dataset>(`/api/datasets/${id}`, dto),

  delete: (id: string): Promise<void> =>
    apiClient.delete<void>(`/api/datasets/${id}`),

  triggerBuild: (id: string): Promise<{ message: string; datasetId: string }> =>
    apiClient.post(`/api/datasets/${id}/build`, {}),

  // ─── Rows ───────────────────────────────────────────────────────────────────

  listRows: (
    datasetId: string,
    opts: { limit?: number; offset?: number; reviewStatus?: string } = {}
  ): Promise<DatasetRowsResponse> => {
    const params = new URLSearchParams();
    if (opts.limit  !== undefined) params.set('limit',  String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    if (opts.reviewStatus)         params.set('reviewStatus', opts.reviewStatus);
    const qs = params.toString();
    return apiClient.get<DatasetRowsResponse>(
      `/api/datasets/${datasetId}/rows${qs ? `?${qs}` : ''}`
    );
  },

  updateRow: (
    datasetId: string,
    rowId: string,
    dto: {
      values?: Record<string, unknown>;
      review_status?: DatasetRowReviewStatus;
      confidence_note?: string;
    }
  ): Promise<DatasetRow> =>
    apiClient.patch<DatasetRow>(`/api/datasets/${datasetId}/rows/${rowId}`, dto),

  exportUrl: (datasetId: string): string =>
    `/api/datasets/${datasetId}/export`,

  suggestSchema: (knowledgeObjectId: string): Promise<DatasetColumn[]> =>
    apiClient.post<DatasetColumn[]>('/api/datasets/suggest-schema', { knowledge_object_id: knowledgeObjectId }),

  suggestSchemaFromOntology: (
    ontologyId: string,
    nodeIds: string[],
    opts?: { datasetName?: string; datasetDescription?: string }
  ): Promise<DatasetColumn[]> =>
    apiClient.post<DatasetColumn[]>('/api/datasets/suggest-schema-from-ontology', {
      ontology_id: ontologyId,
      node_ids: nodeIds,
      dataset_name: opts?.datasetName,
      dataset_description: opts?.datasetDescription,
    }),
};

// Made with Bob
